/**
 * A GUIA DE MOVIMENTAÇÃO EM PAPEL — o documento da operação.
 *
 * Mesma máquina do romaneio: vai para `document.body` por portal, fica
 * invisível na tela, e é a única coisa que a impressora vê. Mesma linguagem
 * visual dos Cadernos. O que muda é o leitor — e por isso muda o conteúdo.
 *
 * ── É PAPEL DE DENTRO DE CASA ─────────────────────────────────────────────
 *
 * O romaneio vai para o comprador e esconde o inventário. Esta guia fica na
 * fazenda: é o gestor conferindo a manhã, o escritório fechando o mês, uma
 * auditoria da IG perguntando de onde veio o peixe do T-4. Então entra o que
 * o romaneio não mostra:
 *
 *   AS VIAGENS, UMA A UMA, COM A HORA. É a parte que torna a operação
 *   auditável — cada linha foi um registro fechado no celular, no momento em
 *   que o cesto desceu. Quem confere contra o caderno do dia confere aqui.
 *
 *   O QUE FICOU NA ORIGEM, dito como o que é: conta do app, otimista por
 *   natureza. Se saiu mais do que o app contava, o número aparece negativo —
 *   é diagnóstico, e o papel não o esconde.
 *
 * ── E O QUILO VEM ESCRITO COMO ESTIMATIVA ─────────────────────────────────
 *
 * Nenhuma balança pesou a carga. Todo quilo desta folha é `peixes × média`,
 * e a coluna diz «estim.» no cabeçalho, sai em itálico e sem o negrito que a
 * tabela dá à última coluna — porque no romaneio a última coluna é dinheiro
 * medido, e aqui é conta (regra 8).
 */

import { createPortal } from 'react-dom'
import type { Guia } from '../dominio/movimentacao'
import { inteiro, numero } from '../dominio/unidades'
import { Dado, emitidoEm, porExtenso } from './Papel'

export function GuiaPapel({ guia }: { guia: Guia }) {
  return createPortal(
    <div className="papel guia-papel">
      <header className="rp-capa">
        <div>
          <div className="rp-antetitulo">Sistema de Gestão · Piscicultura</div>
          <h1>Fazenda Cunhataí</h1>
          <div className="rp-lugar">Ariquemes — Rondônia</div>
        </div>
        <div className="rp-selo">
          <div className="rp-selo-titulo">Guia de movimentação</div>
          <div className="rp-selo-data">{porExtenso(guia.dia)}</div>
        </div>
      </header>

      <section className="rp-ficha">
        <Dado rotulo="Represa de origem" valor={guia.origem.tanque} />
        <Dado rotulo="Ciclo de origem" valor={guia.origem.ciclo} />
        <Dado rotulo="Data" valor={porExtenso(guia.dia)} />
        <Dado rotulo="Peso médio medido" valor={`${numero(guia.mediaG, 0)} g`} />
        <Dado rotulo="Peixes movidos" valor={inteiro(guia.peixes)} />
      </section>

      <table className="rp-tabela">
        <thead>
          <tr>
            <th className="rp-esquerda">Destino</th>
            <th className="rp-esquerda">Ciclo</th>
            <th className="rp-esquerda">Fase</th>
            <th>Viagens</th>
            <th>Peixes</th>
            <th>kg (estim.)</th>
          </tr>
        </thead>
        <tbody>
          {guia.destinos.map((d) => (
            <tr key={d.tanque}>
              <td className="rp-especie">{d.tanque}</td>
              <td className="rp-texto">{d.ciclo ?? 'novo ciclo'}</td>
              <td className="rp-texto">{d.fase}</td>
              <td>{inteiro(d.viagens.length)}</td>
              <td>{inteiro(d.peixes)}</td>
              <td className="rp-estimado">{numero(d.kgEstimado, 1)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th className="rp-especie">Total</th>
            <th />
            <th />
            <th>{inteiro(guia.viagens)}</th>
            <th>{inteiro(guia.peixes)}</th>
            <th className="rp-estimado">{numero(guia.kgEstimado, 1)}</th>
          </tr>
        </tfoot>
      </table>

      <div className="rp-antetitulo rp-secao">As viagens, na ordem em que desceram</div>
      <table className="rp-tabela">
        <thead>
          <tr>
            <th className="rp-esquerda">Hora</th>
            <th className="rp-esquerda">Destino</th>
            <th>Peixes</th>
            <th>kg (estim.)</th>
          </tr>
        </thead>
        <tbody>
          {guia.destinos.flatMap((d) =>
            d.viagens.map((v, i) => (
              <tr key={`${d.tanque}-${i}`}>
                <td className="rp-especie">{v.hora}</td>
                <td className="rp-texto">{d.tanque}</td>
                <td>{inteiro(v.quantidade)}</td>
                <td className="rp-estimado">{numero(v.kgEstimado, 1)}</td>
              </tr>
            )),
          )}
        </tbody>
      </table>

      <section className="rp-fecho">
        <div className="rp-notas">
          <p>
            Peso médio de <strong>{numero(guia.mediaG, 0)} g</strong>, medido na origem por
            amostragem. Os quilos desta guia são calculados a partir dele e <strong>são
            estimativa</strong> — nenhuma balança pesou a carga.
          </p>
          {guia.sobraNaOrigem !== null && (
            <p>
              {guia.sobraNaOrigem >= 0 ? (
                <>
                  Pela conta do app, ficaram cerca de{' '}
                  <strong>{inteiro(guia.sobraNaOrigem)} peixes</strong> na origem — estimativa,
                  e otimista: nem todo peixe morto boia.
                </>
              ) : (
                <>
                  Saíram <strong>{inteiro(-guia.sobraNaOrigem)} peixes a mais</strong> do que o
                  app contava na origem. A diferença é diagnóstico do saldo, não erro da
                  contagem.
                </>
              )}
            </p>
          )}
          {guia.observacao !== '' && <p>{guia.observacao}</p>}
        </div>
      </section>

      <section className="rp-assinaturas">
        <div>
          <div className="rp-risco" />
          <div className="rp-assinatura-rotulo">Operador</div>
        </div>
        <div>
          <div className="rp-risco" />
          <div className="rp-assinatura-rotulo">Responsável</div>
        </div>
      </section>

      <footer className="rp-rodape">
        {emitidoEm()} pelo sistema de gestão da Fazenda Cunhataí. Peixes contados na saída do
        cesto, viagem a viagem, e gravados no momento em que desceram.
      </footer>
    </div>,
    document.body,
  )
}
